import { z } from 'zod';
import { daysBetweenInclusive } from '../dates';
import {
  concurrency,
  currencyCode,
  emptyAsUndefined,
  exchangeRate,
  id,
  listQuery,
  moneyAmount,
  optionalText,
  requiredId,
  ymd,
} from './common';

// ── Revenue entries ─────────────────────────────────────────────────

export const revenueEntryCreateSchema = z.object({
  transactionDate: ymd,
  /** The app this revenue belongs to; the account must be assigned to it on that date. */
  appId: requiredId('App'),
  revenueAccountId: requiredId('Revenue account'),
  /** Amount in the account's currency. 0 is a valid entry ("checked, no revenue"). */
  revenueAmount: moneyAmount,
  /** Override the monthly rate (saved as MANUAL). Omit to use the configured monthly rate. */
  exchangeRate: emptyAsUndefined(exchangeRate),
  notes: optionalText(5000),
});
export type RevenueEntryCreateInput = z.output<typeof revenueEntryCreateSchema>;

/** Date and account identify the daily row and cannot change; delete and re-enter instead. */
export const revenueEntryUpdateSchema = z
  .object({
    revenueAmount: moneyAmount,
    /** A new rate is saved as MANUAL. */
    exchangeRate: emptyAsUndefined(exchangeRate),
    notes: optionalText(5000),
  })
  .partial()
  .extend(concurrency);
export type RevenueEntryUpdateInput = z.output<typeof revenueEntryUpdateSchema>;

const revenueFilters = {
  startDate: ymd.optional(),
  endDate: ymd.optional(),
  appId: id.optional(),
  revenueSourceId: id.optional(),
  revenueAccountId: id.optional(),
  currencyCode: currencyCode.optional(),
  exchangeRateSource: z.enum(['MONTHLY', 'MANUAL']).optional(),
};
export const revenueEntryListSchema = listQuery.extend(revenueFilters);
export const revenueEntrySummarySchema = z.object(revenueFilters);
export type RevenueFilters = z.output<typeof revenueEntrySummarySchema>;

export const revenueEntryBulkSchema = z.object({
  entries: z.array(revenueEntryCreateSchema).min(1).max(500),
  onDuplicate: z.enum(['error', 'overwrite']).default('error'),
});

// ── Morning entry: revenue rows ─────────────────────────────────────

export const morningRevenueRowSchema = z.object({
  appId: requiredId('App'),
  revenueAccountId: requiredId('Revenue account'),
  revenueAmount: moneyAmount,
  /** Only sent when the user overrides the monthly rate. */
  exchangeRate: emptyAsUndefined(exchangeRate),
  entryId: emptyAsUndefined(id),
  updatedAt: z.iso.datetime({ offset: true }).optional(),
});

// ── FX recalculation (admin) ────────────────────────────────────────

export const fxRecalculationSchema = z.object({
  currencyCode,
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
  /** Also re-rate entries saved with a manual rate (normally left alone). */
  includeManual: z.boolean().default(false),
  /** Preview only; nothing is changed. */
  dryRun: z.boolean().default(true),
});
export type FxRecalculationInput = z.output<typeof fxRecalculationSchema>;

// ── Reports ─────────────────────────────────────────────────────────

export const revenueReportSchema = z
  .object({
    startDate: ymd,
    endDate: ymd,
    appId: id.optional(),
  })
  .refine((r) => r.startDate <= r.endDate, {
    path: ['endDate'],
    message: 'End date must be on or after start date',
  })
  .refine((r) => r.startDate > r.endDate || daysBetweenInclusive(r.startDate, r.endDate) <= 1100, {
    path: ['endDate'],
    message: 'Range cannot exceed 1100 days',
  });
